import type { QueryClient } from '@tanstack/react-query';

import type { DebouncedReactionEntry } from './reaction-types';

export const REACTION_MUTATION_FALLBACK_INVALIDATION_DELAY_MS = 1500;
export const REACTION_DEBOUNCE_MS = 2000;

let nextOptimisticReactionId = -1;

// These maps intentionally live outside React so per-PR debounce state survives re-renders.
const fallbackInvalidationTimeoutByPr = new Map<string, ReturnType<typeof setTimeout>>();
const debouncedReactionsByPr = new Map<string, Map<string, DebouncedReactionEntry>>();
const canceledInflightCreatesByPr = new Map<string, Set<string>>();

export function createOptimisticReactionId(): number {
  const reactionId = nextOptimisticReactionId;
  nextOptimisticReactionId -= 1;
  return reactionId;
}

export function getDebouncedEntries(prScopeKey: string) {
  return debouncedReactionsByPr.get(prScopeKey);
}

export function getOrCreateDebouncedEntries(prScopeKey: string) {
  let entries = debouncedReactionsByPr.get(prScopeKey);
  if (!entries) {
    entries = new Map();
    debouncedReactionsByPr.set(prScopeKey, entries);
  }
  return entries;
}

export function getOrCreateCanceledInflightCreateKeys(prScopeKey: string) {
  let canceledKeys = canceledInflightCreatesByPr.get(prScopeKey);
  if (!canceledKeys) {
    canceledKeys = new Set();
    canceledInflightCreatesByPr.set(prScopeKey, canceledKeys);
  }
  return canceledKeys;
}

export function scheduleFallbackInvalidation(
  prScopeKey: string,
  queryClient: QueryClient,
  discussionKey: readonly unknown[],
) {
  const existingTimeout = fallbackInvalidationTimeoutByPr.get(prScopeKey);
  if (existingTimeout) {
    clearTimeout(existingTimeout);
  }

  const timeout = setTimeout(() => {
    fallbackInvalidationTimeoutByPr.delete(prScopeKey);
    pruneReactionScope(prScopeKey);
    void queryClient.invalidateQueries({ queryKey: discussionKey });
  }, REACTION_MUTATION_FALLBACK_INVALIDATION_DELAY_MS);

  fallbackInvalidationTimeoutByPr.set(prScopeKey, timeout);
}

export function pruneReactionScope(prScopeKey: string) {
  const entries = debouncedReactionsByPr.get(prScopeKey);
  if (entries?.size === 0) {
    debouncedReactionsByPr.delete(prScopeKey);
  }

  const canceledKeys = canceledInflightCreatesByPr.get(prScopeKey);
  if (canceledKeys?.size === 0) {
    canceledInflightCreatesByPr.delete(prScopeKey);
  }
}
